import axios from 'axios';
import { mockApi } from './mockApi';
import { tokenService } from './tokenService';
import { logger } from '../utils/logger';
import { resolveApiOrigin } from '../utils/apiOrigin';

// Get the API URL from environment or use default
const isDevelopment = process.env.NODE_ENV === 'development';

// Determine production API URL. Use the same origin as the current page to avoid CORS issues.
const determineProductionApiUrl = (): string => {
    const envValue = process.env.REACT_APP_API_URL?.trim();
    if (envValue) {
        return resolveApiOrigin(envValue);
    }

    // Use the same origin as the current page (whether www or non-www)
    if (typeof window !== 'undefined' && window.location?.origin) {
        return window.location.origin;
    }

    // Fallback to www version
    return 'https://www.gotrutax.com';
};

// FORCE localhost:8000 for development to ensure local backend connection
export const API_URL = isDevelopment ? 'http://localhost:8000' : determineProductionApiUrl();

// Log a warning if production base URL looks suspicious
if (!isDevelopment && API_URL.includes('localhost')) {
    logger.warn('WARNING: Production API URL appears to be localhost. Consider setting REACT_APP_API_URL.');
}

// FORCE NEW BUILD: This is a critical fix for the localhost issue
// The browser is caching the old JavaScript file, so we need a new hash

// Force new build with significant change
const BUILD_VERSION = 'v3.9.0-EMERGENCY-CACHE-BUST'; // EMERGENCY: Force new build hash

// Add a comment to force content change
// This comment is added to force a new build hash
// CRITICAL FIX: Updated to force cache busting
// URGENT: Browser cache issue - forcing new build hash
// EMERGENCY: Browser still loading old JS file - need stronger cache busting
// DEPLOYMENT FIX: Forcing new deployment to resolve Heroku serving old files
// EMERGENCY FIX: Heroku is still serving old index.html - forcing complete rebuild
// FINAL FIX: This should resolve the localhost:8000 issue once and for all
// ULTIMATE FIX: Heroku deployment issue - forcing complete rebuild with new hash
// EMERGENCY CACHE BUST: Browser is loading v3.2.0 instead of v3.8.3 - forcing v3.9.0
// CRITICAL: User seeing localhost:8000 in production - need immediate cache bust
// URGENT DEPLOYMENT: Force new build hash to override browser cache

// Flag to control whether to use the mock API - SET TO FALSE to use real API
const USE_MOCK_API = false; // Real API enabled

logger.log('Environment:', process.env.NODE_ENV);
logger.log('Development mode:', isDevelopment ? 'YES' : 'NO');
logger.log('REACT_APP_API_URL env var:', process.env.REACT_APP_API_URL);
logger.log('Resolved production API URL:', API_URL);
logger.log('Mock API:', USE_MOCK_API ? 'ENABLED' : 'DISABLED');
logger.log('Build version:', BUILD_VERSION);
logger.log('Build timestamp:', new Date().toISOString());
logger.log('CRITICAL: If you see localhost:8000 in production, the browser is using cached JS file');
logger.log('EMERGENCY FIX: Cache busting version deployed - clear browser cache if still seeing localhost');
logger.log('EMERGENCY CACHE BUST v3.9.0: Force browser to reload updated company creation logic');
logger.log('URGENT: Browser cache override - new build hash deployed');

// Create the real API instance
const realApi = axios.create({
    baseURL: API_URL || '/api',
    headers: {
        'Content-Type': 'application/json',
    },
    timeout: 180000, // Increased timeout to 3 minutes for comprehensive tests
});

// Add a request interceptor to add the auth token to requests
realApi.interceptors.request.use(
    async (config) => {
        config.headers = config.headers || {};
        
        // Skip token for login, register, and refresh endpoints
        if (config.url?.includes('/auth/login') || config.url?.includes('/auth/register') || config.url?.includes('/auth/refresh')) {
            logger.log(`API Request: ${config.method?.toUpperCase()} ${config.url} (no token needed)`);
        } else {
            try {
                // Get a valid token, refreshing if necessary
                const token = await tokenService.getValidToken();
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                    logger.log(`API Request: ${config.method?.toUpperCase()} ${config.url} (with valid token)`);
                } else {
                    logger.warn(`API Request: ${config.method?.toUpperCase()} ${config.url} (no token available)`);
                    
                    // CRITICAL FIX: Try to get token directly from localStorage as fallback
                    const fallbackToken = localStorage.getItem('token');
                    if (fallbackToken) {
                        config.headers.Authorization = `Bearer ${fallbackToken}`;
                        logger.log(`API Request: ${config.method?.toUpperCase()} ${config.url} (using fallback token)`);
                    } else {
                        logger.error(`API Request: ${config.method?.toUpperCase()} ${config.url} (no token found anywhere)`);
                    }
                }
            } catch (error) {
                logger.error('Failed to get valid token:', error);
                
                // CRITICAL FIX: Try fallback token from localStorage
                const fallbackToken = localStorage.getItem('token');
                if (fallbackToken) {
                    config.headers.Authorization = `Bearer ${fallbackToken}`;
                    logger.log(`API Request: ${config.method?.toUpperCase()} ${config.url} (using fallback token after error)`);
                } else {
                    logger.error(`API Request: ${config.method?.toUpperCase()} ${config.url} (no fallback token available)`);
                }
            }
        }
        
        // Add API key for all endpoints
        config.headers['X-API-Key'] = process.env.REACT_APP_API_KEY || 'test-key-123';
        
        return config;
    },
    (error) => {
        logger.error('Request interceptor error:', error);
        return Promise.reject(error);
    }
);

// Add a response interceptor to handle token expiration
realApi.interceptors.response.use(
    (response) => {
        // Only log method, URL, and status, not full data
        // logger.log(`API Response: ${response.config.method?.toUpperCase()} ${response.config.url} [${response.status}]`);
        return response;
    },
    async (error) => {
        // Detailed error logging
        if (error.response) {
            // Centralized API error logging (async and batched)
            try {
                logger.logApiError(
                  error.config?.url ?? '',
                  (error.config?.method ?? 'GET').toUpperCase(),
                  error.response?.status ?? 0,
                  error.response?.data ?? String(error)
                );
            } catch {}
            // The request was made and the server responded with a status code
            // that falls out of the range of 2xx
            logger.error('Response error:', {
                status: error.response.status,
                headers: error.response.headers,
                data: error.response.data,
                url: error.config?.url
            });
            
            // Enhanced error logging for validation errors (422)
            if (error.response.status === 422) {
                logger.error('Validation Error Details:', JSON.stringify(error.response.data, null, 2));
                
                // Attach the raw response to the error object for use in components
                error.validationData = error.response.data;
            }
            
            // Handle token expiration and validation errors
            // Only treat a 422 as a token problem when it is actually about the JWT;
            // ordinary form validation errors (e.g. a bad EIN) must not log the user out.
            const reqUrl: string = error.config?.url || '';
            const detailText = JSON.stringify(error.response.data?.detail ?? '').toLowerCase();
            const isTokenError = error.response.status === 401 || (
                error.response.status === 422 && /token|jwt|signature|segments|authorization/.test(detailText)
            );
            // OAuth callbacks are authorized by the one-time state, not the session.
            const isOAuthCallback = /\/oauth\/callback/.test(reqUrl);
            if (isTokenError && !isOAuthCallback && tokenService.getToken()) {
                logger.log(`Token issue detected (${error.response.status}), attempting token refresh`);
                
                // Try to refresh the token
                try {
                    await tokenService.refreshToken();
                    // Retry the original request with the new token
                    const originalRequest = error.config;
                    if (originalRequest) {
                        const newToken = await tokenService.getValidToken();
                        originalRequest.headers.Authorization = `Bearer ${newToken}`;
                        return realApi(originalRequest);
                    }
                } catch (refreshError) {
                    // tokenService.refreshToken() clears the token itself on a real auth failure (401/422).
                    // If the token is still present the refresh was only rate-limited or hit a 5xx/timeout
                    // (e.g. a 503 while the DB bulkhead is saturated): keep the session and surface the error.
                    if (tokenService.getToken()) {
                        logger.warn('Token refresh temporarily unavailable; keeping session', refreshError);
                        return Promise.reject(error);
                    }
                    logger.log('Token refresh failed, redirecting to login');
                    tokenService.removeToken();
                    if (!window.location.pathname.startsWith('/login')) {
                        try {
                            sessionStorage.setItem('gt_post_login_redirect', `${window.location.pathname}${window.location.search}`);
                        } catch {}
                        window.location.href = '/login';
                    }
                }
            }
        } else if (error.request) {
            // The request was made but no response was received
            logger.error('Network error - no response received:', {
                request: error.request,
                url: error.config?.url,
                method: error.config?.method
            });
            
            try {
              logger.logApiError(
                error.config?.url ?? '',
                (error.config?.method ?? 'GET').toUpperCase(),
                0,
                { detail: 'No response received' }
              );
            } catch {}

            // Create a more descriptive and consistent error with status code
            const serverUrl = isDevelopment ? 'http://localhost:8000' : (API_URL || 'the server');
            error.response = {
                status: 0,
                data: {
                    detail: `Network error: Unable to reach the server. Please check your internet connection and try again.`
                }
            };

            // Log more connection details for debugging
            logger.error('Connection details:', {
                apiUrl: API_URL,
                timeout: error.config?.timeout,
                headers: error.config?.headers,
            });
        } else {
            // Something happened in setting up the request that triggered an Error
            logger.error('Request setup error:', error.message);
            try {
              logger.logApiError(
                error.config?.url ?? '',
                (error.config?.method ?? 'GET').toUpperCase(),
                0,
                { detail: error.message || 'Request setup error' }
              );
            } catch {}
            
            // Create a consistent error format
            error.response = {
                status: 0,
                data: {
                    detail: error.message || 'An unknown error occurred'
                }
            };
        }
        
        return Promise.reject(error);
    }
);

// Export either the real API or mock API based on the flag
export const api = USE_MOCK_API ? mockApi : realApi;